
import { useState, useCallback, useEffect } from 'react';
import { Category, Resource, SubTopic, AppSettings } from '../types/resource';
import { toast } from 'sonner';
import { 
  LOCAL_STORAGE_KEYS, 
  saveToLocalStorage, 
  loadData 
} from '../utils/storageUtils';
import { getDefaultImageForType } from '../utils/resourceUtils';

export const useResourceManager = (defaultCategories: Category[], defaultResources: Resource[]) => {
  // State management
  const [categories, setCategories] = useState<Category[]>(() => {
    try {
      return loadData<Category[]>(LOCAL_STORAGE_KEYS.CATEGORIES, defaultCategories);
    } catch (error) {
      console.error('Error loading categories from localStorage:', error);
      return defaultCategories;
    }
  });

  const [resources, setResources] = useState<Resource[]>(() => {
    try {
      return loadData<Resource[]>(LOCAL_STORAGE_KEYS.RESOURCES, defaultResources);
    } catch (error) {
      console.error('Error loading resources from localStorage:', error);
      return defaultResources;
    }
  });

  const [settings, setSettings] = useState<AppSettings>(() => {
    try {
      const storedSettings = localStorage.getItem(LOCAL_STORAGE_KEYS.APP_SETTINGS);
      if (storedSettings) {
        const parsed = JSON.parse(storedSettings);
        if (typeof parsed === 'object' && parsed !== null && 'developerMode' in parsed) {
          console.log('Loaded settings from localStorage:', parsed);
          return parsed;
        }
      }
      const defaultSettings = { developerMode: false };
      console.log('Using default settings (dev mode off):', defaultSettings);
      return defaultSettings;
    } catch (error) {
      console.error('Error loading app settings:', error);
      return { developerMode: false };
    }
  });

  const [isAdmin, setIsAdmin] = useState<boolean>(() => {
    try {
      const savedAuth = localStorage.getItem(LOCAL_STORAGE_KEYS.IS_ADMIN);
      return savedAuth ? JSON.parse(savedAuth) : false;
    } catch (error) {
      console.error('Error loading auth state from localStorage:', error);
      return false;
    }
  });

  const [credentials, setCredentials] = useState<{username: string, password: string}>(() => {
    try {
      const savedCredentials = localStorage.getItem(LOCAL_STORAGE_KEYS.ADMIN_CREDENTIALS);
      if (savedCredentials) {
        const parsed = JSON.parse(savedCredentials);
        if (typeof parsed === 'object' && parsed !== null && 'username' in parsed && 'password' in parsed) {
          console.log('Loaded valid credentials from localStorage');
          return parsed;
        }
        console.error('Invalid credentials format in localStorage');
      }
      return { username: 'admin', password: 'password' };
    } catch (error) {
      console.error('Error loading credentials from localStorage:', error);
      return { username: 'admin', password: 'password' };
    }
  });

  // Persist data when state changes
  useEffect(() => {
    if (categories) {
      saveToLocalStorage(LOCAL_STORAGE_KEYS.CATEGORIES, categories);
    }
  }, [categories]);

  useEffect(() => {
    if (resources) {
      saveToLocalStorage(LOCAL_STORAGE_KEYS.RESOURCES, resources);
    }
  }, [resources]);

  useEffect(() => {
    saveToLocalStorage(LOCAL_STORAGE_KEYS.IS_ADMIN, isAdmin);
  }, [isAdmin]);

  useEffect(() => {
    console.log('Saving updated credentials to localStorage:', credentials);
    saveToLocalStorage(LOCAL_STORAGE_KEYS.ADMIN_CREDENTIALS, credentials);
  }, [credentials]);

  // Authentication functions
  const login = async (username: string, password: string): Promise<boolean> => {
    if (settings.developerMode) {
      setIsAdmin(true);
      saveToLocalStorage(LOCAL_STORAGE_KEYS.IS_ADMIN, true);
      return true;
    }
    
    console.log('Attempting login with:', username, 'Current credentials:', credentials);
    
    if (username === credentials.username && password === credentials.password) {
      setIsAdmin(true);
      saveToLocalStorage(LOCAL_STORAGE_KEYS.IS_ADMIN, true);
      return true;
    }
    return false;
  };

  const logout = () => {
    setIsAdmin(false);
    saveToLocalStorage(LOCAL_STORAGE_KEYS.IS_ADMIN, false);
  };

  const toggleDeveloperMode = () => {
    setSettings(prev => {
      const isProductionEnv = typeof window !== 'undefined' && window.__IS_PRODUCTION__ === true;
      
      if (isProductionEnv) {
        console.log('Developer mode cannot be enabled in production');
        if (prev.developerMode) {
          toast.info("Developer mode is disabled in production");
        }
        const newSettings = { ...prev, developerMode: false };
        saveToLocalStorage(LOCAL_STORAGE_KEYS.APP_SETTINGS, newSettings);
        return newSettings;
      }
      
      const newSettings = { ...prev, developerMode: !prev.developerMode };
      console.log('Toggling developer mode:', prev.developerMode, '->', newSettings.developerMode);
      
      if (newSettings.developerMode) {
        setIsAdmin(true);
        saveToLocalStorage(LOCAL_STORAGE_KEYS.IS_ADMIN, true);
        toast.success("Developer mode enabled - Admin access granted");
      } else {
        setIsAdmin(false);
        saveToLocalStorage(LOCAL_STORAGE_KEYS.IS_ADMIN, false);
        toast.info("Developer mode disabled - Admin login required");
      }
      
      saveToLocalStorage(LOCAL_STORAGE_KEYS.APP_SETTINGS, newSettings);
      return newSettings;
    });
  };

  const changeCredentials = (newUsername: string, newPassword: string) => {
    console.log('Changing credentials to:', newUsername);
    const newCredentials = {
      username: newUsername,
      password: newPassword
    };
    
    const saved = saveToLocalStorage(LOCAL_STORAGE_KEYS.ADMIN_CREDENTIALS, newCredentials);
    
    if (saved) {
      setCredentials(newCredentials);
      setIsAdmin(false);
      saveToLocalStorage(LOCAL_STORAGE_KEYS.IS_ADMIN, false);
      toast.success("Credentials updated. Please log in with your new credentials.");
    } else {
      toast.error("Failed to update credentials. Please try again.");
    }
  };

  // Category management functions
  const addCategory = (category: Omit<Category, 'id'>) => {
    try {
      const newCategory = {
        ...category,
        id: Date.now().toString()
      };
      setCategories(prevCategories => [...prevCategories, newCategory]);
      console.log('Category added:', newCategory);
      toast.success("Category added successfully");
    } catch (error) {
      console.error('Error adding category:', error);
      toast.error("Failed to add category");
    }
  };

  const updateCategory = (id: string, category: Partial<Category>) => {
    try {
      console.log('Updating category:', id, 'with data:', category);
      setCategories(prevCategories => {
        const existingCategory = prevCategories.find(cat => cat.id === id);
        if (!existingCategory) {
          console.error('Category not found:', id);
          return prevCategories;
        }
        
        const updatedCategories = prevCategories.map(cat => {
          if (cat.id === id) {
            const updatedCategory = { 
              ...cat, 
              ...category,
              bannerImageUrl: category.bannerImageUrl !== undefined ? category.bannerImageUrl : cat.bannerImageUrl,
              imageUrl: category.imageUrl !== undefined ? category.imageUrl : cat.imageUrl
            };
            console.log('Updated category:', updatedCategory);
            return updatedCategory;
          }
          return cat;
        });
        return updatedCategories;
      });
      console.log('Category updated:', id);
    } catch (error) {
      console.error('Error updating category:', error);
      toast.error("Failed to update category");
    }
  };

  const deleteCategory = (id: string) => {
    try {
      setCategories(prevCategories => 
        prevCategories.filter(cat => cat.id !== id)
      );
      
      setResources(prevResources => 
        prevResources.filter(resource => resource.categoryId !== id)
      );
      console.log('Category deleted:', id);
      toast.success("Category deleted successfully");
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error("Failed to delete category");
    }
  };

  // Resource management functions
  const addResource = (resource: Omit<Resource, 'id'>) => {
    try {
      const resourceWithImage = {
        ...resource,
        imageUrl: resource.imageUrl || getDefaultImageForType(resource.type)
      };
      
      const newResource = {
        ...resourceWithImage,
        id: Date.now().toString()
      };
      
      setResources(prevResources => [...prevResources, newResource]);
      console.log('Resource added:', newResource);
      toast.success("Resource added successfully");
    } catch (error) {
      console.error('Error adding resource:', error);
      toast.error("Failed to add resource");
    }
  };

  const updateResource = (id: string, resource: Partial<Resource>) => {
    try {
      setResources(prevResources => {
        const updatedResources = prevResources.map(res => {
          if (res.id === id) {
            const updatedResource = { 
              ...res, 
              ...resource,
              imageUrl: resource.imageUrl !== undefined ? resource.imageUrl : res.imageUrl || getDefaultImageForType(res.type)
            };
            return updatedResource;
          }
          return res;
        });
        return updatedResources;
      });
      console.log('Resource updated:', id);
    } catch (error) {
      console.error('Error updating resource:', error);
      toast.error("Failed to update resource");
    }
  };

  const deleteResource = (id: string) => {
    try {
      setResources(prevResources => 
        prevResources.filter(res => res.id !== id)
      );
      console.log('Resource deleted:', id);
      toast.success("Resource deleted successfully");
    } catch (error) {
      console.error('Error deleting resource:', error);
      toast.error("Failed to delete resource");
    }
  };

  // SubTopic management functions
  const addSubTopic = (resourceId: string, subTopic: Omit<SubTopic, 'id'>) => {
    try {
      const newSubTopic = {
        ...subTopic,
        id: Date.now().toString()
      };
      
      setResources(prevResources => {
        return prevResources.map(resource => {
          if (resource.id === resourceId) {
            const existingSubTopics = resource.subTopics || [];
            return {
              ...resource,
              subTopics: [...existingSubTopics, newSubTopic]
            };
          }
          return resource;
        });
      });
      
      console.log('SubTopic added:', newSubTopic);
      toast.success("Sub-topic added successfully");
    } catch (error) {
      console.error('Error adding subtopic:', error);
      toast.error("Failed to add sub-topic");
    }
  };

  const removeSubTopic = (resourceId: string, subTopicId: string) => {
    try {
      setResources(prevResources => {
        return prevResources.map(resource => {
          if (resource.id === resourceId && resource.subTopics) {
            return {
              ...resource,
              subTopics: resource.subTopics.filter(subTopic => subTopic.id !== subTopicId)
            };
          }
          return resource;
        });
      });
      console.log('SubTopic removed:', subTopicId);
      toast.success("Sub-topic removed successfully");
    } catch (error) {
      console.error('Error removing subtopic:', error);
      toast.error("Failed to remove sub-topic");
    }
  };

  const updateSubTopic = (resourceId: string, subTopicId: string, updates: Partial<SubTopic>) => {
    try {
      console.log('Updating subtopic:', subTopicId, 'with data:', updates);
      
      setResources(prevResources => {
        return prevResources.map(resource => {
          if (resource.id === resourceId && resource.subTopics) {
            const updatedSubTopics = resource.subTopics.map(subTopic => {
              if (subTopic.id === subTopicId) {
                const updatedSubTopic = { 
                  ...subTopic, 
                  ...updates,
                  title: updates.title !== undefined ? updates.title : subTopic.title,
                  description: updates.description !== undefined ? updates.description : subTopic.description,
                  link: updates.link !== undefined ? updates.link : subTopic.link,
                  htmlEmbed: updates.htmlEmbed !== undefined ? updates.htmlEmbed : subTopic.htmlEmbed,
                  imageUrl: updates.imageUrl !== undefined ? updates.imageUrl : subTopic.imageUrl
                };
                console.log('Updated subtopic:', updatedSubTopic);
                return updatedSubTopic;
              }
              return subTopic;
            });
            
            return {
              ...resource,
              subTopics: updatedSubTopics
            };
          }
          return resource;
        });
      });
      console.log('SubTopic updated:', subTopicId);
    } catch (error) {
      console.error('Error updating subtopic:', error);
      toast.error("Failed to update sub-topic");
    }
  };

  // Query functions
  const getResourcesByCategory = useCallback((categoryId: string) => {
    return resources.filter(resource => resource.categoryId === categoryId);
  }, [resources]);

  const getResourceById = useCallback((id: string) => {
    return resources.find(resource => resource.id === id);
  }, [resources]);

  const getDefaultResource = useCallback((id: string) => {
    return defaultResources.find(resource => resource.id === id);
  }, [defaultResources]);

  const getCategoryById = useCallback((id: string) => {
    return categories.find(category => category.id === id);
  }, [categories]);

  // Timestamp for triggers updates
  const [_timestamp, setTimestamp] = useState<number>(Date.now());
  
  useEffect(() => {
    setTimestamp(Date.now());
  }, [settings, isAdmin, categories.length, resources.length]);

  return {
    // State
    categories,
    resources,
    isAdmin,
    settings,
    _timestamp,
    
    // Auth methods
    login,
    logout,
    changeCredentials,
    toggleDeveloperMode,
    
    // Category methods
    addCategory,
    updateCategory,
    deleteCategory,
    
    // Resource methods
    addResource,
    updateResource,
    deleteResource,
    
    // SubTopic methods
    addSubTopic,
    removeSubTopic,
    updateSubTopic,
    
    // Query methods
    getResourcesByCategory,
    getResourceById,
    getDefaultResource,
    getCategoryById
  };
};
